import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { FileUpload, type FileUploadSelectEvent } from 'primereact/fileupload';
import { type Toast } from 'primereact/toast';
import OpenAPI from '../../../endpoint/Openapi';
import { type paths } from '../../../endpoint/Schema';
import { normalizeString } from '../../../utils/GeneralUtils';
import { type CustomError } from '../../../models/Error';

// Interface for selected image files with preview
interface SelectedImage {
  file: File;
  previewUrl: string;
  name: string;
}

// Interface for existing server images
interface ExistingImage {
  id?: string;
  url: string;
  name: string;
  index: number;
  isDeleted: boolean;
}

// Type for AppleStoreConfig
type AppleStoreConfig = NonNullable<
  NonNullable<
    paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
  >['appleStoreConfig']
>;

// Type for AppleStoreConfigInformationDto
type AppleStoreInfo = NonNullable<
  AppleStoreConfig['appleStoreConfigInformation']
>;

// Type for FeatureSettings
type FeatureSettings =
  paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json'];

interface AppleStoreTabProps {
  appleConfig: AppleStoreConfig;
  featureSettings: FeatureSettings | undefined;
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<FeatureSettings | undefined>
  >;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  tenantId: string | undefined;
  toast: React.RefObject<Toast>;
  fetchSettings: () => Promise<void>;
  activeTabIndex: number;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
}

export default function AppleStoreTab({
  appleConfig,
  featureSettings,
  setFeatureSettings,
  loading,
  setLoading,
  tenantId,
  toast,
  fetchSettings,
  activeTabIndex,
  setActiveTabIndex,
}: AppleStoreTabProps) {
  // State for selected images with previews
  const [appleSelectedImages, setAppleSelectedImages] = React.useState<
    SelectedImage[]
  >([]);

  // Refs for FileUpload components to clear them after selection
  const appleFileUploadRef = React.useRef<FileUpload>(null);

  // Keys to force FileUpload component re-render when needed
  const [appleUploadKey, setAppleUploadKey] = React.useState(0);

  // State for existing server screenshots
  const [existingImages, setExistingImages] = React.useState<ExistingImage[]>(
    [],
  );

  // Handlers for Apple Store Config
  const handleAppleConfigChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFeatureSettings((prev: FeatureSettings | undefined) => {
      const prevInfo: Partial<AppleStoreInfo> =
        prev?.appleStoreConfig?.appleStoreConfigInformation ?? {};
      const nextInfo: AppleStoreInfo = {
        appName: name === 'appName' ? (value ?? '') : (prevInfo?.appName ?? ''),
        subtitle:
          name === 'subtitle' ? (value ?? '') : (prevInfo?.subtitle ?? ''),
        decription:
          name === 'decription' ? (value ?? '') : (prevInfo?.decription ?? ''),
        keyWord: name === 'keyWord' ? (value ?? '') : (prevInfo?.keyWord ?? ''),
        marketingUrl:
          name === 'marketingUrl'
            ? (value ?? '')
            : (prevInfo?.marketingUrl ?? ''),
        promotionText:
          name === 'promotionText'
            ? (value ?? '')
            : (prevInfo?.promotionText ?? ''),
        supportUrl:
          name === 'supportUrl' ? (value ?? '') : (prevInfo?.supportUrl ?? ''),
        copyRight:
          name === 'copyRight' ? (value ?? '') : (prevInfo?.copyRight ?? ''),
        videoLink:
          name === 'videoLink' ? (value ?? '') : (prevInfo?.videoLink ?? ''),
      };
      return {
        ...prev,
        appleStoreConfig: {
          ...prev?.appleStoreConfig,
          appleStoreConfigInformation: nextInfo,
        },
      };
    });
  };

  // File upload handlers with PrimeReact FileUpload
  const handleAppleScreenshotsSelect = (e: FileUploadSelectEvent) => {
    const files = e.files;
    if (!files || files.length === 0) return;

    const newImages: SelectedImage[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    // Add new images to existing ones instead of replacing
    setAppleSelectedImages((prev) => [...prev, ...newImages]);

    // Note: We don't update featureSettings here to avoid affecting existing screenshots display
    // The new images will be handled separately when saving

    // Force FileUpload component to re-render by changing key
    setAppleUploadKey((prev) => prev + 1);
  };

  // Remove image handler
  const handleRemoveAppleImage = (index: number) => {
    setAppleSelectedImages((prev) => {
      const imageToRemove = prev[index];
      // Clean up the object URL to prevent memory leaks
      URL.revokeObjectURL(imageToRemove.previewUrl);
      return prev.filter((_, i) => i !== index);
    });

    // Note: We don't update featureSettings here since new images are handled separately
  };

  // Clear all images handler
  const handleClearAllAppleImages = () => {
    // Clean up all object URLs
    appleSelectedImages.forEach((image) => {
      URL.revokeObjectURL(image.previewUrl);
    });

    setAppleSelectedImages([]);

    // Note: We don't update featureSettings here since new images are handled separately
  };

  // Remove existing server image handler
  const handleRemoveExistingImage = (imageIndex: number) => {
    setExistingImages((prev) =>
      prev.map((img) => {
        // Mark the specific image for deletion using its index
        if (img.index === imageIndex) {
          return { ...img, isDeleted: true };
        }
        return img;
      }),
    );
  };

  // Save Apple screenshots function
  async function saveAppleScreenshots() {
    if (
      !tenantId ||
      (appleSelectedImages.length === 0 &&
        existingImages.filter((img) => img.isDeleted).length === 0)
    )
      return;

    setLoading(true);

    try {
      // Prepare DTO with new images and deleted existing images
      const newImageDtos = appleSelectedImages.map((image, index) => ({
        file: image.file as unknown as string,
        order: index,
        isDeleted: false,
      }));

      const deletedImageDtos = existingImages
        .filter((img) => img.isDeleted)
        .map((image) => ({
          id: image.id, // Include the server-provided ID for precise deletion
          file: null, // No file for existing images being deleted
          order: image.index, // Maintain the original order/index
          isDeleted: true, // Mark as deleted
        }));

      const allDtos = [...newImageDtos, ...deletedImageDtos];

      const res = await OpenAPI.POST(
        '/api/v2/admin/feature-setting/apple-screen-shot',
        {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            dto: allDtos,
          },
          bodySerializer: (body) => {
            if (!body?.dto) return body;
            const formData = new FormData();
            body.dto.forEach((item, index: number) => {
              // For new images (uploads)
              if (item.file && !item.isDeleted) {
                formData.append(
                  `dto[${index}].file`,
                  item.file as unknown as File,
                );
              }
              // For existing images being deleted, include the ID
              if (item.id !== undefined && item.id !== null) {
                formData.append(`dto[${index}].id`, item.id);
              }
              // Always include order/index
              if (item.order !== undefined) {
                formData.append(`dto[${index}].order`, item.order.toString());
              }
              // Always include deletion status
              if (item.isDeleted !== undefined) {
                formData.append(
                  `dto[${index}].isDeleted`,
                  item.isDeleted.toString(),
                );
              }
            });
            return formData;
          },
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Apple screenshots updated successfully',
        life: 3000,
      });

      // Clear the uploaded images since they're now saved
      setAppleSelectedImages([]);
      setAppleUploadKey((prev) => prev + 1);

      // Remove the deleted images from the existing images list
      setExistingImages((prev) => prev.filter((img) => !img.isDeleted));

      // Refresh settings to get updated server screenshots
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Sync with App Store function
  async function syncWithAppStore() {
    if (!tenantId) return;
    setLoading(true);

    try {
      const res = await OpenAPI.GET(
        '/api/v2/admin/feature-setting/sync-apple-screen-shot',
        {
          params: {
            query: {
              tenantId,
            },
          },
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Synced with App Store successfully',
        life: 3000,
      });

      // Refresh the settings to get updated screenshots
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Save App Information function
  async function saveAppInformation() {
    if (!tenantId) return;

    // Normalize and validate required fields
    const appName = normalizeString(
      appleConfig?.appleStoreConfigInformation?.appName,
    );
    const subtitle = normalizeString(
      appleConfig?.appleStoreConfigInformation?.subtitle,
    );
    const marketingUrl = normalizeString(
      appleConfig?.appleStoreConfigInformation?.marketingUrl,
    );
    const supportUrl = normalizeString(
      appleConfig?.appleStoreConfigInformation?.supportUrl,
    );
    const copyRight = normalizeString(
      appleConfig?.appleStoreConfigInformation?.copyRight,
    );

    // Check for empty required fields
    const emptyFields: string[] = [];
    if (!appName) emptyFields.push('App Name');
    if (!subtitle) emptyFields.push('Subtitle');
    if (!marketingUrl) emptyFields.push('Marketing URL');
    if (!supportUrl) emptyFields.push('Support URL');
    if (!copyRight) emptyFields.push('Copyright');

    if (emptyFields.length > 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: `Please fill in required fields: ${emptyFields.join(', ')}`,
        life: 5000,
      });
      return;
    }

    setLoading(true);

    try {
      // Normalize optional fields, use null if empty
      const description = normalizeString(
        appleConfig?.appleStoreConfigInformation?.decription,
      );
      const keyWord = normalizeString(
        appleConfig?.appleStoreConfigInformation?.keyWord,
      );
      const promotionText = normalizeString(
        appleConfig?.appleStoreConfigInformation?.promotionText,
      );
      const videoLink = normalizeString(
        appleConfig?.appleStoreConfigInformation?.videoLink,
      );

      const res = await OpenAPI.POST('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          appleStoreConfig: {
            appleStoreConfigInformation: {
              appName: appName as string, // We've validated these are not empty
              subtitle: subtitle as string,
              marketingUrl: marketingUrl as string,
              supportUrl: supportUrl as string,
              copyRight: copyRight as string,
              decription: description ?? null,
              keyWord: keyWord ?? null,
              promotionText: promotionText ?? null,
              videoLink: videoLink ?? null,
            },
          },
        },
      });

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'App Information saved successfully',
        life: 3000,
      });

      if (res.data) {
        setFeatureSettings(res.data);
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Sync App Store Information function
  async function syncAppStoreInformation() {
    if (!tenantId) return;
    setLoading(true);

    try {
      const res = await OpenAPI.GET(
        '/api/v2/admin/feature-setting/sync-app-store-information',
        {
          params: {
            query: {
              tenantId,
            },
          },
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'App Store Information synced successfully',
        life: 3000,
      });

      // Refresh the settings to get updated app information
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Cleanup object URLs when component unmounts
  React.useEffect(() => {
    return () => {
      appleSelectedImages.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
    };
  }, [appleSelectedImages]);

  // Load existing screenshots from featureSettings
  React.useEffect(() => {
    if (featureSettings?.appleStoreConfig?.appScreenShot) {
      const screenshots = featureSettings.appleStoreConfig.appScreenShot;
      const baseUrl = process.env.REACT_APP_API_BASE_URI ?? '';
      const existingScreenshots: ExistingImage[] = [];

      screenshots.forEach((screenshot, index) => {
        // Handle new AppScreenShotDto structure with id and url
        if (
          typeof screenshot === 'object' &&
          screenshot !== null &&
          'url' in screenshot
        ) {
          const screenshotObj = screenshot as { id?: string; url: string };
          const url = screenshotObj.url;
          if (typeof url === 'string') {
            existingScreenshots.push({
              id: screenshotObj.id,
              url: url.startsWith('http') ? url : `${baseUrl}${url}`,
              name: `Screenshot ${index + 1}`,
              index,
              isDeleted: false,
            });
          }
        }
      });

      setExistingImages(existingScreenshots);
    } else {
      setExistingImages([]);
    }
  }, [featureSettings]);

  return (
    <TabView
      activeIndex={activeTabIndex}
      onTabChange={(e) => {
        setActiveTabIndex(e.index);
      }}
    >
      <TabPanel header="📝 App Information">
        <div className="flex flex-col gap-y-6">
          <div className="w-[40%] max-w-xl min-w-96">
            <div>App Name *</div>
            <InputText
              name="appName"
              value={appleConfig?.appleStoreConfigInformation?.appName ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="Enter app name"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Subtitle *</div>
            <InputText
              name="subtitle"
              value={appleConfig?.appleStoreConfigInformation?.subtitle ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="Enter subtitle"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Keywords</div>
            <InputText
              name="keyWord"
              value={appleConfig?.appleStoreConfigInformation?.keyWord ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="Enter keywords (comma separated)"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Marketing URL *</div>
            <InputText
              name="marketingUrl"
              value={
                appleConfig?.appleStoreConfigInformation?.marketingUrl ?? ''
              }
              onChange={handleAppleConfigChange}
              placeholder="https://example.com"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Support URL *</div>
            <InputText
              name="supportUrl"
              value={appleConfig?.appleStoreConfigInformation?.supportUrl ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="https://support.example.com"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Copyright *</div>
            <InputText
              name="copyRight"
              value={appleConfig?.appleStoreConfigInformation?.copyRight ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="© 2025 Company Name"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Promotion Text</div>
            <InputText
              name="promotionText"
              value={
                appleConfig?.appleStoreConfigInformation?.promotionText ?? ''
              }
              onChange={handleAppleConfigChange}
              placeholder="Enter promotional text"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Video Link</div>
            <InputText
              name="videoLink"
              value={appleConfig?.appleStoreConfigInformation?.videoLink ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="https://video.example.com"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-full">
            <div>Description</div>
            <InputTextarea
              name="decription"
              value={appleConfig?.appleStoreConfigInformation?.decription ?? ''}
              onChange={handleAppleConfigChange}
              placeholder="Enter app description"
              rows={4}
              className="mt-3 w-full"
            />
          </div>

          {/* Save and Sync App Information buttons */}
          <div className="w-full">
            <div className="flex justify-end gap-3 mt-6">
              <Button
                label="Sync with App Store"
                icon="pi pi-sync"
                className="p-button-outlined p-button-info"
                onClick={syncAppStoreInformation}
                loading={loading}
                disabled={loading}
              />
              <Button
                label="Save App Information"
                icon="pi pi-save"
                className="text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none"
                onClick={saveAppInformation}
                loading={loading}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel header="📸 Screenshots">
        <div className="flex flex-col gap-y-6">
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-medium">Upload App Screenshots</div>
              <div className="flex gap-2">
                {/* Sync with App Store button */}
                {existingImages.length > 0 && (
                  <Button
                    label="Sync with App Store"
                    icon="pi pi-sync"
                    className="p-button-sm p-button-outlined p-button-info"
                    onClick={syncWithAppStore}
                    loading={loading}
                    disabled={loading}
                  />
                )}
                {/* Save button for changes */}
                {(appleSelectedImages.length > 0 ||
                  existingImages.some((img) => img.isDeleted)) && (
                  <Button
                    label={
                      appleSelectedImages.length > 0 &&
                      existingImages.some((img) => img.isDeleted)
                        ? 'Save Changes'
                        : appleSelectedImages.length > 0
                          ? 'Save Apple Screenshots'
                          : 'Apply Deletions'
                    }
                    icon="pi pi-save"
                    className="text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none"
                    onClick={saveAppleScreenshots}
                    loading={loading}
                    disabled={loading}
                  />
                )}
                {appleSelectedImages.length > 0 && (
                  <Button
                    label="Clear All"
                    icon="pi pi-times"
                    className="p-button-sm p-button-outlined p-button-danger"
                    onClick={handleClearAllAppleImages}
                  />
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              You can upload multiple screenshots for the Apple App Store.
              Supported formats: JPG, PNG, GIF
            </p>
            <FileUpload
              key={appleUploadKey}
              ref={appleFileUploadRef}
              mode="basic"
              name="apple-screenshots"
              multiple
              accept="image/*"
              onSelect={handleAppleScreenshotsSelect}
              chooseLabel="Choose Screenshots"
              className="w-full"
              auto={false}
            />
          </div>

          {/* New Screenshots to Upload - Show First */}
          {appleSelectedImages.length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  New Screenshots to Upload ({appleSelectedImages.length})
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {appleSelectedImages.map(
                  (image: SelectedImage, index: number) => (
                    <div
                      key={index}
                      className="relative bg-blue-50 border border-blue-200 rounded-lg overflow-hidden group"
                    >
                      {/* Image Preview */}
                      <div className="aspect-[9/16] w-full relative">
                        <img
                          src={image.previewUrl}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveAppleImage(index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-blue-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-blue-600">
                          Screenshot {index + 1}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Existing Screenshots from Server - Show After New Screenshots */}
          {existingImages.filter((img) => !img.isDeleted).length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  Current Screenshots (
                  {existingImages.filter((img) => !img.isDeleted).length})
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {existingImages
                  .filter((img) => !img.isDeleted)
                  .map((image: ExistingImage) => (
                    <div
                      key={image.index}
                      className="relative bg-green-50 border border-green-200 rounded-lg overflow-hidden group"
                    >
                      {/* Image Preview */}
                      <div className="aspect-[9/16] w-full relative">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveExistingImage(image.index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Mark for deletion"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-green-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-green-600">
                          Server Image
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </TabPanel>
    </TabView>
  );
}
